import { t, translateError } from "./i18n";

/** Everything the app knows comes from the restaurant's server; amounts are integer cents, as in the POS. */
export type Option = { id: string; name: string; priceMinor: number };
export type OptionGroup = { id: string; name: string; min: number; max: number; options: Option[] };
export type Item = {
  id: string; categoryId: string; name: string; description: string; priceMinor: number; sizes: Option[]; modifierGroups: OptionGroup[];
  /** False outside the hours the dish is served. */
  available: boolean; /** Run out for today. */ soldOut: boolean; image: string;
  /** A promotion running on this dish right now ("20% off"), taken off automatically when ordered. */
  offer: string | null; /** Added to the menu in the last two weeks. */ isNew: boolean;
};
export type Offer = { id: string; title: string; text: string; until: string | null; /** The discount it gives, when linked to a promotion. */ discount: string };
export type Shop = {
  offers: Offer[];
  restaurant: { name: string; phone: string; addressLines: string[]; openingHours: string }; open: boolean; closedMessage: string; closedReason: "paused" | "closed" | ""; payOnline: boolean;
  categories: { id: string; name: string }[]; items: Item[];
};
export type Totals = { grossMinor: number; discountMinor: number; subtotalMinor: number; serviceChargeMinor: number; taxMinor: number; totalMinor: number };
export type Discount = { label: string; amountMinor: number };
export type OrderView = {
  token: string; dailyNumber: string; ref: string; createdAt: string; status: "open" | "completed" | "voided";
  paymentState: "unpaid" | "partial" | "paid" | "partially_refunded" | "refunded" | "complimentary";
  /** Null when the kitchen does not report progress on a screen. */
  kitchenState: "pending" | "preparing" | "ready" | "served" | null; fulfillmentState: "open" | "collected" | "served";
  /** False until staff have accepted the order; then `readyAt` is when they expect it to be ready and `message` anything they wrote. */
  confirmed: boolean; readyAt: string | null; message: string;
  /** Waiting at the counter. */
  ready: boolean;
  /** Seconds left to cancel in the app, counted by the server when this view was made. */
  cancelSecondsLeft: number; cancelledByCustomer: boolean;
  lines: { name: string; sizeName: string | null; modifiers: string[]; quantity: number; note: string; amountMinor: number; /** Struck off by the restaurant: it had run out. */ unavailable: boolean }[];
  discounts: Discount[]; totals: Totals; paidMinor: number; dueMinor: number; payOnline: boolean;
};
export type CartLine = { key: string; itemId: string; sizeId?: string | undefined; optionIds: string[]; quantity: number; note: string };

// The development server never talks to the live restaurant: it uses the sample one from `npm run shop:dev -w @mango-moons/api`.
const API = (import.meta.env.VITE_SHOP_API_URL || (import.meta.env.DEV ? "http://localhost:8788" : import.meta.env.VITE_API_URL) || "").replace(/\/+$/, "");

export class ShopError extends Error {
  /** `offline` when the server could not be reached at all, as opposed to it saying no. */
  constructor(message: string, readonly offline = false) { super(message); }
}

async function request<T>(path: string, body?: unknown): Promise<T> {
  let response: Response;
  try { response = await fetch(API + path, body === undefined ? {} : { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) }); }
  catch { throw new ShopError(translateError("No connection."), true); }
  const data = await response.json().catch(() => null) as { ok?: boolean; result?: T; error?: { message?: string } } | null;
  if (!response.ok || !data) throw new ShopError(data?.error?.message ? translateError(data.error.message) : t("genericError"));
  return (data.ok ? data.result : data) as T;
}

const toLines = (cart: readonly CartLine[]) => cart.map(({ itemId, sizeId, optionIds, quantity, note }) => ({ menuItemId: itemId, ...(sizeId ? { sizeId } : {}), modifierOptionIds: optionIds, quantity, note }));

/**
 * The menu as last seen on this phone, so the app opens at once and refreshes in the background.
 * Prices and stock are always checked again by the server when the order is placed.
 */
export const cachedShop = () => read<Shop | null>("mm.order.menu", null);
export const fetchShop = () => request<Shop>("/shop/menu").then((shop) => { write("mm.order.menu", shop); return shop; });
export const fetchQuote = (cart: readonly CartLine[]) => request<{ totals: Totals; discounts: Discount[] }>("/shop/quote", { lines: toLines(cart) });
export const placeOrder = (input: { idempotencyKey: string; customerName: string; customerPhone: string; note: string; cart: readonly CartLine[] }) =>
  request<{ token: string; dailyNumber: string; ref: string; totalMinor: number }>("/shop/order", { idempotencyKey: input.idempotencyKey, customerName: input.customerName, customerPhone: input.customerPhone, note: input.note, lines: toLines(input.cart) });
export const cancelOrder = (token: string) => request<OrderView>("/shop/cancel", { token });
export const fetchOrder = (token: string) => request<OrderView>(`/shop/order?token=${token}`);

/** Sends the customer to PayHere's checkout page; they come back to this order afterwards. */
export async function payOnline(token: string): Promise<void> {
  const { action, fields } = await request<{ action: string; fields: Record<string, string> }>("/shop/pay", { token });
  const form = Object.assign(document.createElement("form"), { method: "post", action });
  for (const [name, value] of Object.entries(fields)) form.append(Object.assign(document.createElement("input"), { type: "hidden", name, value }));
  document.body.append(form);
  form.submit();
}

/**
 * In the Android app, asks the phone to keep an eye on this order and notify the customer when the
 * restaurant confirms it, when it is ready, or if it is cancelled - even with the app closed.
 * In a browser there is nothing to ask; the order page updates itself while it is open.
 */
export function watchOrder(token: string) {
  try { (window as unknown as { MangoOrderNative?: { watch(token: string): void } }).MangoOrderNative?.watch(token); } catch { /* an older shell without notifications */ }
}

/**
 * The Android app's own version, from the text it adds to the browser identity ("MangoMoonsOrder/0.4.0").
 * Null in a normal browser, where there is nothing to update.
 */
export const shellVersion = () => /MangoMoonsOrder\/(\d+\.\d+\.\d+)/.exec(navigator.userAgent)?.[1] ?? null;
const newer = (a: string, b: string) => { const x = a.split(".").map(Number), y = b.split(".").map(Number); for (let i = 0; i < 3; i++) if ((x[i] ?? 0) !== (y[i] ?? 0)) return (x[i] ?? 0) > (y[i] ?? 0); return false; };
/** The newer Android app to offer, when this phone runs an older one. */
export async function availableUpdate(): Promise<{ version: string; url: string } | null> {
  const current = shellVersion();
  if (!current) return null;
  try {
    const latest = await (await fetch("/app-version.json", { cache: "no-store" })).json() as { version: string; url: string };
    return newer(latest.version, current) ? latest : null;
  } catch { return null; }
}

// ---------------------------------------------------------------- what the phone remembers
function read<T>(key: string, fallback: T): T {
  try { return JSON.parse(localStorage.getItem(key) ?? "null") as T ?? fallback; } catch { return fallback; }
}
const write = (key: string, value: unknown) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* private browsing: the app still works for this visit */ } };

export type Profile = { name: string; phone: string };
export const loadProfile = () => read<Profile>("mm.order.profile", { name: "", phone: "" });
export const saveProfile = (profile: Profile) => write("mm.order.profile", profile);
export const loadCart = () => read<CartLine[]>("mm.order.cart", []);
export const saveCart = (cart: readonly CartLine[]) => write("mm.order.cart", cart);
/** Everything this app saved on the phone: name, number, basket, recent orders and the copy of the menu. */
export function forgetThisPhone() {
  for (const key of ["mm.order.profile", "mm.order.cart", "mm.order.history", "mm.order.menu"]) { try { localStorage.removeItem(key); } catch { /* nothing saved */ } }
}
/** There are no accounts: an order belongs to whoever holds its token, so recent ones are kept on the phone. */
export type PastOrder = { token: string; dailyNumber: string; totalMinor: number; at: string };
export const loadPastOrders = () => read<PastOrder[]>("mm.order.history", []);
export const rememberOrder = (order: PastOrder) => write("mm.order.history", [order, ...loadPastOrders().filter((entry) => entry.token !== order.token)].slice(0, 15));

// ---------------------------------------------------------------- small helpers
export function money(minor: number): string {
  const whole = Math.trunc(Math.abs(minor) / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const cents = Math.abs(minor) % 100;
  return `Rs ${minor < 0 ? "-" : ""}${whole}${cents ? `.${String(cents).padStart(2, "0")}` : ""}`;
}
const clock = new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Colombo", hour: "numeric", minute: "2-digit", hour12: true });
/** "6:45 pm", in the restaurant's time wherever the phone thinks it is. */
export const readyTime = (iso: string) => clock.format(new Date(iso));
export const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(" ");
/** Digits only, in the local 10-digit form: a number pasted or auto-filled as +94 77… becomes 077…. */
export const normalisePhone = (text: string) => text.replace(/^\s*(?:\+94|0094)/, "0").replace(/\D/g, "").replace(/^94(?=\d{9}$)/, "0");
export const validPhone = (text: string) => /^0\d{9}$/.test(text);

export function linePrice(item: Item, line: Pick<CartLine, "sizeId" | "optionIds">): number {
  const options = item.modifierGroups.flatMap((group) => group.options).filter((option) => line.optionIds.includes(option.id));
  return (item.sizes.find((size) => size.id === line.sizeId)?.priceMinor ?? item.priceMinor) + options.reduce((sum, option) => sum + option.priceMinor, 0);
}
export function lineSummary(item: Item, line: Pick<CartLine, "sizeId" | "optionIds">): string {
  const options = item.modifierGroups.flatMap((group) => group.options).filter((option) => line.optionIds.includes(option.id)).map((option) => option.name);
  return [item.sizes.find((size) => size.id === line.sizeId)?.name, ...options].filter(Boolean).join(" · ");
}
